import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarSync, Link2, LoaderCircle, RefreshCw, Unlink } from "lucide-react";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { GoogleCalendarService } from "@/services/googleCalendarService";

const MEETING_QUERY_KEYS = [
  "reunioes",
  "reunioes-expansao",
  "reunioes-calendario",
  "reunioes-calendario-all",
];
const OAUTH_TIMEOUT_MS = 2 * 60 * 1000;
const SYNC_TIMEOUT_MS = 30 * 1000;
const SYNC_POLL_INTERVAL_MS = 1500;

type Props = {
  compact?: boolean;
};

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function GoogleCalendarConnection({ compact = false }: Props) {
  const queryClient = useQueryClient();
  const popupRef = useRef<Window | null>(null);
  const pollRef = useRef<number | null>(null);
  const popupTimeoutRef = useRef<number | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const empresaId = localStorage.getItem("empresaId");

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["google-calendar-user-status", empresaId],
    queryFn: async () => {
      const response = await GoogleCalendarService.getStatus();
      if (response?.status !== 200) {
        throw new Error(response?.data?.message || "Não foi possível consultar o Google Agenda.");
      }
      return response.data;
    },
    staleTime: 30_000,
    retry: 1,
  });

  async function refreshCalendarData() {
    await refetch();
    await Promise.all(
      MEETING_QUERY_KEYS.map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      ),
    );
  }

  function stopPopupPolling() {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (popupTimeoutRef.current !== null) {
      window.clearTimeout(popupTimeoutRef.current);
      popupTimeoutRef.current = null;
    }
  }

  useEffect(() => {
    return () => {
      stopPopupPolling();
      popupRef.current?.close();
    };
  }, []);

  async function connect() {
    setConnecting(true);
    try {
      const response = await GoogleCalendarService.getAuthUrl();
      if (response?.status !== 200 || !response.data?.url) {
        throw new Error(response?.data?.message || "Não foi possível iniciar a conexão.");
      }

      const popup = window.open(
        response.data.url,
        "google-calendar-connect",
        "width=560,height=720,noopener=no,noreferrer=no",
      );
      if (!popup) {
        throw new Error("Permita pop-ups para conectar o Google Agenda.");
      }

      popupRef.current = popup;
      stopPopupPolling();
      popupTimeoutRef.current = window.setTimeout(() => {
        stopPopupPolling();
        popup.close();
        popupRef.current = null;
        setConnecting(false);
        toast.error("A conexão com o Google expirou. Tente novamente.");
      }, OAUTH_TIMEOUT_MS);
      pollRef.current = window.setInterval(async () => {
        try {
          const callbackResult = popup.location.href
            ? new URL(popup.location.href).searchParams.get("google_calendar")
            : null;
          if (callbackResult) {
            popup.close();
            if (callbackResult !== "success") {
              toast.error("Não foi possível conectar o Google Agenda.");
            } else {
              toast.success("Google Agenda conectado.");
            }
          }
        } catch {
          // Enquanto o popup está no Google, sua URL não pode ser lida por segurança.
        }
        if (!popup.closed) return;
        stopPopupPolling();
        popupRef.current = null;
        setConnecting(false);
        await refreshCalendarData();
      }, 500);
    } catch (error: unknown) {
      setConnecting(false);
      toast.error(errorMessage(error, "Erro ao conectar o Google Agenda."));
    }
  }

  async function syncNow() {
    setSyncing(true);
    try {
      const response = await GoogleCalendarService.sync();
      if (response?.status !== 202) {
        throw new Error(response?.data?.message || "Não foi possível iniciar a sincronização.");
      }
      toast.info("Sincronização com o Google Agenda iniciada.");
      const deadline = Date.now() + SYNC_TIMEOUT_MS;
      while (Date.now() < deadline) {
        await new Promise((resolve) =>
          window.setTimeout(resolve, SYNC_POLL_INTERVAL_MS),
        );
        const statusResponse = await GoogleCalendarService.getStatus();
        if (statusResponse?.status !== 200) {
          throw new Error(
            statusResponse?.data?.message ||
              "Não foi possível acompanhar a sincronização.",
          );
        }
        if (statusResponse.data.sync_status === "error") {
          throw new Error(
            "O Google Agenda não conseguiu sincronizar. Tente novamente.",
          );
        }
        if (statusResponse.data.sync_status !== "pending") {
          await refreshCalendarData();
          toast.success("Google Agenda sincronizado.");
          return;
        }
      }
      throw new Error(
        "A sincronização está demorando mais que o esperado. Tente novamente em instantes.",
      );
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Erro ao sincronizar o Google Agenda."));
    } finally {
      setSyncing(false);
    }
  }

  function disconnect() {
    confirmAlert({
      title: "Desconectar Google Agenda",
      message:
        "Novas alterações deixarão de ser sincronizadas. As reuniões já importadas serão preservadas.",
      buttons: [
        {
          label: "Desconectar",
          onClick: async () => {
            setDisconnecting(true);
            try {
              const response = await GoogleCalendarService.disconnect();
              if (response?.status !== 200) {
                throw new Error(
                  response?.data?.message ||
                    "Erro ao desconectar o Google Agenda.",
                );
              }
              toast.success("Google Agenda desconectado.");
              await refreshCalendarData();
            } catch (disconnectError: unknown) {
              toast.error(
                errorMessage(
                  disconnectError,
                  "Erro ao desconectar o Google Agenda.",
                ),
              );
            } finally {
              setDisconnecting(false);
            }
          },
        },
        { label: "Cancelar", onClick: () => undefined },
      ],
    });
  }

  if (isLoading) {
    return (
      <span className="inline-flex items-center gap-2 text-xs text-cinza-500">
        <LoaderCircle size={14} className="animate-spin" /> Google Agenda
      </span>
    );
  }

  if (isError) {
    return (
      <button
        type="button"
        onClick={() => void refetch()}
        title={errorMessage(error, "Falha ao consultar o Google Agenda")}
        className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 transition hover:bg-red-100"
      >
        <RefreshCw size={14} /> {!compact && "Tentar novamente"}
      </button>
    );
  }

  if (!data?.connected) {
    return (
      <button
        type="button"
        onClick={connect}
        disabled={connecting}
        title="Conectar sua agenda pessoal"
        className="inline-flex items-center gap-2 rounded-full border border-azul-400/30 bg-white px-3 py-2 text-xs font-medium text-azul-400 transition hover:bg-azul-400/5 disabled:opacity-60"
      >
        {connecting ? (
          <LoaderCircle size={14} className="animate-spin" />
        ) : (
          <Link2 size={14} />
        )}
        {!compact && (connecting ? "Conectando..." : "Conectar Google Agenda")}
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-green-600/20 bg-green-50 px-2 py-1.5">
      <CalendarSync size={14} className="shrink-0 text-green-700" />
      {!compact && (
        <span className="max-w-40 truncate text-xs font-medium text-green-800" title={data.email ?? ""}>
          {data.email || "Google Agenda conectado"}
        </span>
      )}
      <button
        type="button"
        onClick={syncNow}
        disabled={syncing || disconnecting || data.sync_status === "pending"}
        title={data.sync_status === "error" ? "Tentar sincronizar novamente" : "Sincronizar agora"}
        className="rounded-full p-1 text-green-800 transition hover:bg-green-100 disabled:opacity-50"
      >
        <RefreshCw size={13} className={syncing || data.sync_status === "pending" ? "animate-spin" : ""} />
      </button>
      <button
        type="button"
        onClick={disconnect}
        disabled={disconnecting}
        title="Desconectar Google Agenda"
        className="rounded-full p-1 text-red-600 transition hover:bg-red-50 disabled:opacity-50"
      >
        {disconnecting ? (
          <LoaderCircle size={13} className="animate-spin" />
        ) : (
          <Unlink size={13} />
        )}
      </button>
    </div>
  );
}
